import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, Plus, X, Loader2, ArrowUpRight, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { MODULES } from "./modulesConfig";

export default function ModuleLinks({ moduleKey, item, onRefresh }) {
  const links = item.links || [];
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(() => {
      setLoading(true);
      api.get(`/mod/search`, { params: { q } })
        .then((r) => setResults((r.data.results || []).filter((x) => !(x.module_key === moduleKey && x.id === item.id) && !links.some((l) => l.id === x.id && l.module_key === x.module_key))))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]); // eslint-disable-line

  const attach = async (r) => {
    try {
      await api.post(`/mod/${moduleKey}/items/${item.id}/links`, { target_module: r.module_key, target_id: r.id });
      toast.success("Vinculado");
      setAdding(false); setQ(""); setResults([]);
      onRefresh && onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const detach = async (l) => {
    try { await api.delete(`/mod/${moduleKey}/items/${item.id}/links/${l.id}`); onRefresh && onRefresh(); }
    catch { toast.error("Error"); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4" data-testid="module-links">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 overline text-navy/40"><Link2 className="w-3.5 h-3.5" /> Vinculado con otras apps</div>
        <button onClick={() => setAdding((v) => !v)} className="btn btn-ghost text-sm" data-testid="link-add-toggle"><Plus className="w-3.5 h-3.5" /> Vincular</button>
      </div>

      {adding && (
        <div className="mb-3 space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 text-navy/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca un registro de cualquier app…"
              className="w-full bg-white border border-navy/10 rounded-lg pl-10 pr-3 py-2 outline-none focus:border-violet text-sm" data-testid="link-search-input" autoFocus />
            {loading && <Loader2 className="w-4 h-4 animate-spin text-navy/40 absolute right-3 top-1/2 -translate-y-1/2" />}
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {results.map((r) => {
              const cfg = MODULES[r.module_key];
              return (
                <button key={`${r.module_key}-${r.id}`} onClick={() => attach(r)}
                  className="w-full text-left flex items-center justify-between gap-2 border border-navy/10 rounded-lg px-3 py-2 hover:border-violet hover:bg-navy/[0.02]"
                  data-testid={`link-option-${r.id}`}>
                  <span className="min-w-0"><span className="text-sm text-navy/85 truncate block">{r.label}</span><span className="text-[11px] text-navy/40">{cfg?.title || r.module_key}</span></span>
                  <Plus className="w-4 h-4 text-navy/30 shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {links.length === 0 && !adding ? (
        <div className="text-xs text-navy/35">Aún no hay referencias. Vincula un proyecto, documento o reunión de otra suite.</div>
      ) : (
        <div className="space-y-1.5">
          {links.map((l) => {
            const cfg = MODULES[l.module_key];
            return (
              <div key={l.id} className="flex items-center justify-between gap-2 border border-navy/10 rounded-lg px-3 py-2" data-testid={`linked-${l.id}`}>
                <Link to={`/os/mod/${l.module_key}`} className="min-w-0 group flex items-center gap-2">
                  <span className="min-w-0"><span className="text-sm text-navy/85 truncate block group-hover:text-violet">{l.label}</span><span className="text-[11px] text-navy/40">{cfg?.title || l.module_key}</span></span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-navy/30 group-hover:text-violet shrink-0" />
                </Link>
                <button onClick={() => detach(l)} className="text-navy/25 hover:text-destructive shrink-0" data-testid={`link-remove-${l.id}`}><X className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
